import type { CompRef } from '@wix/document-services-types';
import type {
  DisplayGroups,
  EditorReactComponent,
} from '@wix/component-protocol/schema';
import { ComponentsPlatformService } from './ComponentsPlatformService';
import type { ResetStatesOptions } from './StatesPlatformService';

import type { IVirtualManifestBuilderProps } from '../api';
import type { ChangeType } from '../ManifestChangeDetection';
import type { ActionName } from '../types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class BackwardCompatibleComponentsPlatformService {
  constructor(private readonly componentsService: ComponentsPlatformService) {}

  getPublicManifestAPI() {
    return {
      buildManifestAPI: (props: IVirtualManifestBuilderProps) => {
        return this.componentsService.buildManifestAPI(props);
      },
      getAutoInjectedEditorPointers: (
        editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
      ) =>
        this.componentsService.containers.getAutoInjectedEditorPointers(
          editorPointers,
        ),
      arrayItems: {
        getSelectedArrayIndex: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) =>
          this.componentsService.arrayItems.getArrayItemsSelectedIndex(
            editorPointers,
          ),
        setSelectedArrayIndex: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          index: number,
        ) =>
          this.componentsService.arrayItems.setArrayItemsSelectedIndex(
            editorPointers,
            index,
          ),
        resetSelectedArrayIndex: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) =>
          this.componentsService.arrayItems.resetArrayItemsSelectedIndex(
            editorPointers,
          ),
        resetAllSelectedArrayIndexes: () =>
          this.componentsService.arrayItems.resetAllArrayItemsSelectedIndexes(),
      },
      menuItems: {
        getSelectedMenuItemPath: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) =>
          this.componentsService.menuItems.getSelectedMenuItemPath(
            editorPointers,
          ),
        setSelectedMenuItemPath: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          path: string[],
        ) =>
          this.componentsService.menuItems.setSelectedMenuItemPath(
            editorPointers,
            path,
          ),
        resetSelectedMenuItemPath: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) =>
          this.componentsService.menuItems.resetSelectedMenuItemPath(
            editorPointers,
          ),
      },
      behaviors: {
        is: {
          removable: (editorPointer: EditorPointer<CompRef>) =>
            this.componentsService.behaviors.getValue(
              editorPointer,
              'removable',
            ) ?? false,
          selectable: (editorPointer: EditorPointer<CompRef>) =>
            this.componentsService.behaviors.getValue(
              editorPointer,
              'selectable',
            ) ?? false,
          duplicatable: (editorPointer: EditorPointer<CompRef>) =>
            this.componentsService.layout.isDuplicatable(editorPointer) ?? true,
        },
      },
      getDefinitionDescriptions: (type: string) =>
        this.componentsService.definitionDescriptions.getByType(type),
      get: {
        /**
         * @deprecated ⚠️ DANGEROUS: This method is unstable and may lead to unexpected behavior. DON'T USE IT IN PRODUCTION CODE.
         */
        getAll: () => this.componentsService.getAllManifests(),
        editorElement: (
          componentType: string,
          options?: { preset?: string; state?: string },
        ) =>
          this.componentsService.manifest.getManifestByComponentType(
            componentType,
            options,
          ),
        getPendingAppsManifests: () =>
          this.componentsService.getPendingAppsManifests(),
        displayName: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getDisplayName(editorPointers),
        selector: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getSelector(editorPointer),
        style: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getStyle(editorPointers),
        getColorStyle: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getColorStyle(editorPointers),
        presets: (
          componentType: string,
          options?: {
            breakpoint?: keyof NonNullable<
              NonNullable<EditorReactComponent['installation']>['presets']
            >;
          },
        ) =>
          this.componentsService.manifest.getPresetsByComponentType(
            componentType,
            options,
          ),
        displayGroups: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          options?: {
            filter?: { groupType?: DisplayGroups[string]['groupType'] };
          },
        ) =>
          this.componentsService.components.getDisplayGroups(
            editorPointers,
            options,
          ),
        data: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getData(editorPointers),
        help: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getHelp(editorPointer),
        interactions: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getInteractions(editorPointer),
        getMediaData: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getMediaData(editorPointers),
        editActions: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getEditActions(editorPointers),
        customActions: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getCustomActions(editorPointers),
        archetype: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getArchetype(editorPointer),
        containerData: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getContainerData(editorPointer),
        defaultData: (
          editorPointer: EditorPointer<CompRef>,
          dataItemId: string,
        ) =>
          this.componentsService.components.getDefaultDataValue(
            editorPointer,
            dataItemId,
          ),
        defaultStyle: (
          editorPointer: EditorPointer<CompRef>,
          styleItemId: string,
        ) =>
          this.componentsService.components.getDefaultStyleValue(
            editorPointer,
            styleItemId,
          ),
        defaultStyles: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getDefaultStyleValues(
            editorPointer,
          ),
        installation: (componentType: string) =>
          this.componentsService.manifest.getInstallationByComponentType(
            componentType,
          ),
        fixedPosition: (componentType: string) =>
          this.componentsService.manifest.getFixedPositionByComponentType(
            componentType,
          ),
        initialSize: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.components.getInitialSize(editorPointer),
        initialSizeByComponentType: (
          componentType: string,
          options?: {
            breakpoint?: keyof NonNullable<
              NonNullable<EditorReactComponent['installation']>['presets']
            >;
            preset?: string;
          },
        ) =>
          this.componentsService.manifest.getInitialSizeByComponentType(
            componentType,
            options,
          ),
        layoutByComponentType: (
          componentType: string,
          options?: {
            breakpoint?: keyof NonNullable<
              NonNullable<EditorReactComponent['installation']>['presets']
            >;
            preset?: string;
          },
        ) =>
          this.componentsService.manifest.getLayoutByComponentType(
            componentType,
            options,
          ),
      },
      layout: {
        get: {
          resizeDirection: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'resizeDirection',
            ),
          contentResizeDirection: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'contentResizeDirection',
            ),
          contentFill: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'contentFill',
            ),
        },
        is: {
          stretchingDisabled: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'disableStretching',
            ) ?? null,
          positioningDisabled: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'disablePositioning',
            ) ?? null,
          rotationDisabled: (
            editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          ) =>
            this.componentsService.layout.getValue(
              editorPointers,
              'disableRotation',
            ) ?? null,
        },
      },
      presets: {
        getAppliedPreset: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getAppliedPreset(editorPointers),
        getPresetDefinitions: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.components.getPresets(editorPointers),
      },
      states: {
        getStateFocusedElement: (
          editorPointer: EditorPointer<CompRef>,
          stateKey?: string,
        ) =>
          this.componentsService.states.getStateFocusedElement(
            editorPointer,
            stateKey,
          ),
        getStateDefinitions: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.states.getStateDefinitions(editorPointers),
        getDesignStateDefinitions: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) =>
          this.componentsService.states.getDesignStateDefinitions(
            editorPointers,
          ),
        getState: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        ) => this.componentsService.states.getState(editorPointers),
        setState: (
          editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          stateKey: string,
        ) => this.componentsService.states.setState(editorPointers, stateKey),
        resetStates: (
          editorPointer: EditorPointer<CompRef> | EditorPointer<CompRef>[],
          options?: ResetStatesOptions,
        ) => this.componentsService.states.resetStates(editorPointer, options),
        getActiveStates: () => this.componentsService.states.getActiveStates(),
        resetAllStates: () => this.componentsService.states.resetAllStates(),
        activateVisibilityState: (editorPointer: EditorPointer<CompRef>) =>
          this.componentsService.states.activateVisibilityState(editorPointer),
        setDesignStates: (
          editorPointers: EditorPointer<CompRef>[],
          states: string[],
        ) =>
          this.componentsService.states.setDesignStates(editorPointers, states),
        getDesignStates: (editorPointers: EditorPointer<CompRef>[]): string[] =>
          this.componentsService.states.getDesignStates(editorPointers),
        resetDesignStates: (editorPointers: EditorPointer<CompRef>[]) =>
          this.componentsService.states.resetDesignStates(editorPointers),
      },
      manifest: {
        isBoxLike: (type: string) =>
          this.componentsService.manifest.isBoxLike(type),
        getDesignStateDefinitionsByComponentType: (componentType: string) =>
          this.componentsService.manifest.getDesignStateDefinitionsByComponentType(
            componentType,
          ),
      },
      waitForManifests: (compTypes: string[], options?: { timeout?: number }) =>
        this.componentsService.waitForManifests(compTypes, options),
      isBuilderComponentType: (compType: string) =>
        this.componentsService.applicationData.isBuilderComponentType(compType),
      isBoxLike: (editorPointer: EditorPointer<CompRef>) =>
        this.componentsService.hierarchy.isBoxLike(editorPointer),
      onChange: (
        editorPointers: EditorPointer<CompRef>[],
        cb: (patch: { type: ChangeType; value: unknown }) => void,
      ) => this.componentsService.components.onChange(editorPointers, cb),
    };
  }

  getPublicHierarchyAPI() {
    return {
      getChildren: (
        editorPointer: EditorPointer<CompRef>,
        options?: {
          isRecursive?: boolean;
          filterHiddenElements?: boolean;
          filter?: {
            displayFilters?: boolean;
            stageVisibility?: boolean;
          };
        },
      ) => this.componentsService.hierarchy.getChildren(editorPointer, options),
      getAncestors: (editorPointer: EditorPointer<CompRef>) =>
        this.componentsService.hierarchy.getAncestors(editorPointer),
      getActionableElements: (
        editorPointer: EditorPointer<CompRef> | EditorPointer<CompRef>[],
        actionName: ActionName,
      ) =>
        this.componentsService.actionableElements.getActionableElements(
          editorPointer,
          actionName,
        ),
      isElementActionable: (
        editorPointer: EditorPointer<CompRef>,
        actionName: ActionName,
      ) =>
        this.componentsService.actionableElements.isElementActionable(
          editorPointer,
          actionName,
        ),
    };
  }

  static getPact() {
    const pact: PublicAPI<BackwardCompatibleComponentsPlatformService> = {
      getPublicManifestAPI: () =>
        mockPublicManifestAPI as unknown as ReturnType<
          BackwardCompatibleComponentsPlatformService['getPublicManifestAPI']
        >,
      getPublicHierarchyAPI: () =>
        mockPublicHierarchyAPI as unknown as ReturnType<
          BackwardCompatibleComponentsPlatformService['getPublicHierarchyAPI']
        >,
      [Symbol.for('pactBuilderServices')]: true,
    };
    return pact as BackwardCompatibleComponentsPlatformService;
  }
}

type PublicKeys<T> = {
  [K in keyof T]: T[K] extends (...args: any[]) => any
    ? K
    : T[K] extends object
    ? K extends string
      ? K
      : never
    : K;
}[keyof T];

type PublicAPI<T> = Pick<T, PublicKeys<T>>;

export const mockPublicManifestAPI = {
  buildManifestAPI: () => null,
  getAutoInjectedEditorPointers: () => [],
  arrayItems: {
    getSelectedArrayIndex: () => null,
    setSelectedArrayIndex: () => {},
    resetSelectedArrayIndex: () => {},
    resetAllSelectedArrayIndexes: () => {},
  },
  menuItems: {
    getSelectedMenuItemPath: () => undefined,
    setSelectedMenuItemPath: () => {},
    resetSelectedMenuItemPath: () => {},
  },
  behaviors: {
    is: {
      removable: () => false,
      selectable: () => false,
      duplicatable: () => true,
    },
  },
  manifest: {
    isBoxLike: () => false,
  },
  getDefinitionDescriptions: () => null,
  get: {
    getAll: () => ({}),
    editorElement: () => null,
    getPendingAppsManifests: () => Promise.resolve({}),
    displayName: () => null,
    selector: () => null,
    style: () => null,
    getColorStyle: () => null,
    presets: () => null,
    displayGroups: () => null,
    data: () => null,
    help: () => null,
    interactions: () => null,
    getMediaData: () => null,
    editActions: () => null,
    customActions: () => null,
    archetype: () => null,
    containerData: () => null,
    defaultData: () => null,
    defaultStyle: () => null,
    defaultStyles: () => ({
      cssProperties: {},
      cssCustomProperties: {},
    }),
    installation: () => null,
    fixedPosition: () => null,
    initialSize: () => null,
    initialSizeByComponentType: () => null,
    layoutByComponentType: () => null,
  },
  layout: {
    get: {
      resizeDirection: () => null,
      contentResizeDirection: () => null,
      contentFill: () => null,
    },
    is: {
      stretchingDisabled: () => null,
      positioningDisabled: () => null,
      rotationDisabled: () => null,
    },
  },
  presets: {
    getAppliedPreset: () => null,
    getPresetDefinitions: () => null,
  },
  states: {
    getStateFocusedElement: () => null,
    getStateDefinitions: () => ({}),
    getDesignStateDefinitions: () => ({}),
    getState: () => null,
    setState: () => {},
    activateVisibilityState: () => {},
    resetStates: () => {},
    getActiveStates: () => [],
    resetAllStates: () => {},
    setDesignStates: () => {},
    getDesignStates: () => [],
    resetDesignStates: () => {},
  },
  waitForManifests: () => Promise.resolve(),
  isBuilderComponentType: () => false,
  isBoxLike: () => false,
  onChange: () => () => {},
};

export const mockPublicHierarchyAPI = {
  getChildren: () => [],
  getAncestors: () => [],
  getActionableElements: () => null,
  isElementActionable: () => false,
};
